import { restClient } from '@/Core/Services/RestClientService'
import type { DispatcherOption, UserCollectionItem } from '@/Ship/Contracts/DashboardSummary'

/**
 * Load active dispatchers (ROLE_DISPATCHER + ROLE_SENIOR_DISPATCHER) for the
 * dashboard filter dropdown.
 *
 * Implementation notes:
 *   - `filterRoles` is stored as a JSON-serialized array in a single column,
 *     so role lookup must use LIKE per role (see UserRepository::loadByRoles).
 *   - The backend `filters` query parameter is JSON-encoded: `and: [...]` for
 *     hard predicates and `or: [...]` for role alternatives.
 *   - `limit: 50` is a UX cap — the dropdown is searchable, so users with more
 *     dispatchers than fit can type a few letters and re-narrow via `q`.
 *
 * Pass `search` to narrow results by name. Returns dispatcher options ready
 * to drop into `<Select options={...} />`.
 */
export const LoadDispatchersTask = async (search?: string): Promise<DispatcherOption[]> => {
    const filters = JSON.stringify({
        and: [{ field: 'status', op: '=', value: 'active' }],
        or: [
            { field: 'filterRoles', op: 'like', value: 'ROLE_DISPATCHER' },
            { field: 'filterRoles', op: 'like', value: 'ROLE_SENIOR_DISPATCHER' },
        ],
    })

    const params: Record<string, string> = { filters, limit: '50' }
    if (search && search.trim()) params['q'] = search.trim()

    const req = restClient.get('/services/users', { searchParams: params }, { forceRefresh: true })
    if (!req) return []

    const data = (await req) as unknown as { items?: UserCollectionItem[] } | UserCollectionItem[]
    const items = Array.isArray(data) ? data : (data?.items ?? [])
    return items.map((u) => ({ value: u.id, label: userToLabel(u), ...(u.uuid ? { uuid: u.uuid } : {}) }))
}

/**
 * Human-readable user label for the dropdown.
 * Falls back through several name fields before resorting to `Користувач #<id>`.
 */
const userToLabel = (user: UserCollectionItem): string => {
    return (
        user.shortName ||
        user.fullName ||
        [user.lastName, user.firstName].filter(Boolean).join(' ') ||
        `Користувач #${user.id}`
    )
}
